/**
 * Camera-blocked states — honest, recovery-stepped card (PermissionGate
 * conventions). Extracted VERBATIM from DocumentCapture so the plan-driven
 * capture steps (SELFIE / SELFIE_WITH_DOC — INTAKE-09 §3) share the exact same
 * honest copy; INTAKE-08 §A1 pins these cards as the model for rejection
 * honesty ("preserve verbatim").
 */

import { useT } from '@/i18n/runtime';
import type { useCaptureCamera } from '@/media/useCaptureCamera';
import { Icon } from './Icon';

export function BlockedCameraCard({
  cam,
}: { cam: ReturnType<typeof useCaptureCamera> }): JSX.Element {
  const { t } = useT();
  let bTitle: string;
  let bCopy: string;
  switch (cam.phase) {
    case 'denied':
      bTitle = t('droom.cam.denied.title');
      bCopy = t('droom.cam.denied.body');
      break;
    case 'os-denied':
      bTitle = t('droom.cam.osdenied.title');
      bCopy = t('droom.cam.osdenied.body');
      break;
    case 'no-device':
      bTitle = t('droom.cam.nodevice.title');
      // The hook reports a deck KEY (or nothing) — translate either way.
      bCopy = cam.detail ? t(cam.detail) : t('droom.cam.nodevice.fallback');
      break;
    case 'webview':
      bTitle = t('droom.cam.webview.title');
      bCopy = t('droom.cam.webview.body');
      break;
    default:
      bTitle = t('droom.cam.unsupported.title');
      bCopy = t('droom.cam.unsupported.body');
  }
  const showSteps = cam.phase === 'denied' || cam.phase === 'os-denied';
  return (
    <div className="card pad" style={{ textAlign: 'center' }}>
      <span className="perm-gate-badge">
        <Icon name={cam.phase === 'webview' ? 'monitor' : 'camera'} size={26} />
      </span>
      <div className="h-title sm">{bTitle}</div>
      <p className="sub-sm" style={{ marginTop: 8 }}>
        {bCopy}
      </p>
      {showSteps ? (
        <ol className="perm-steps" aria-label={t('droom.perm.steps.aria')}>
          {cam.recoverySteps.map((s) => (
            <li key={s}>{t(s)}</li>
          ))}
        </ol>
      ) : null}
      {cam.phase === 'webview' ? (
        <p className="perm-autoresume mono" style={{ marginTop: 10 }}>
          {t('droom.perm.webview.tip')}
        </p>
      ) : null}
    </div>
  );
}
